// src/pages/PagesUtilisateurs/DashboardUsers/modules/ObjectifsSaisonDisplay.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaCheck,
  FaEdit,
  FaPlus,
  FaTrash,
} from "react-icons/fa";

import { toast } from "react-toastify";

import {
  createObjectif,
  deleteObjectif,
  getObjectifsByAdherent,
  setObjectifDone,
  setObjectifProgress,
  updateObjectif,
} from "../../../../services/objectifsServices";

import { getAdherentsByUser } from "../../../../services/adherentServices";

import CreateEditObjectifModal from "../CreateEditObjectifModal";

import DashboardActionButton from "../ui/DashboardActionButton";
import DashboardEmptyState from "../ui/DashboardEmptyState";

import { FaBullseye } from "react-icons/fa";

function Pill({
  children,
  tone = "slate",
}) {
  const classes = {
    slate:
      "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",

    blue:
      "bg-blue-50 text-[#013E8E] dark:bg-blue-500/10 dark:text-blue-300",

    emerald:
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
  };

  return (
    <span
      className={`
        inline-flex rounded-full
        px-2.5 py-1
        text-[10px] font-bold
        ${classes[tone] || classes.slate}
      `}
    >
      {children}
    </span>
  );
}

function Progress({
  value = 0,
}) {
  const progress = Math.max(
    0,
    Math.min(
      100,
      Number(value) || 0,
    ),
  );

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between text-xs">
        <span className="font-bold text-slate-500 dark:text-slate-400">
          Progression
        </span>

        <span className="font-black text-[#013E8E] dark:text-blue-300">
          {progress} %
        </span>
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div
          className="h-full rounded-full bg-[#013E8E] transition-all dark:bg-blue-500"
          style={{
            width: `${progress}%`,
          }}
        />
      </div>
    </div>
  );
}

function ObjectifCard({
  objectif,
  onEdit,
  onDelete,
  onToggleDone,
  onChangeProgress,
}) {
  return (
    <article
      className={`
        rounded-2xl border
        bg-white p-4
        transition
        dark:bg-slate-900
        ${
          objectif.is_done
            ? "border-emerald-200 dark:border-emerald-500/20"
            : "border-slate-200 dark:border-slate-800"
        }
      `}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap gap-2">
            {objectif.is_done ? (
              <Pill tone="emerald">
                Terminé
              </Pill>
            ) : (
              <Pill tone="blue">
                En cours
              </Pill>
            )}

            {objectif.type ? (
              <Pill>
                {objectif.type}
              </Pill>
            ) : null}

            {objectif.saison ? (
              <Pill>
                Saison{" "}
                {objectif.saison}
              </Pill>
            ) : null}
          </div>

          <h4 className="mt-3 font-black text-slate-950 dark:text-white">
            {objectif.title}
          </h4>

          {objectif.description ? (
            <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
              {
                objectif.description
              }
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 gap-1.5">
          <DashboardActionButton
            compact
            icon={FaCheck}
            variant={
              objectif.is_done
                ? "success"
                : "neutral"
            }
            onClick={() =>
              onToggleDone(
                objectif,
              )
            }
            title={
              objectif.is_done
                ? "Marquer comme non terminé"
                : "Marquer comme terminé"
            }
          />

          <DashboardActionButton
            compact
            icon={FaEdit}
            onClick={() =>
              onEdit(objectif)
            }
            title="Modifier"
          />

          <DashboardActionButton
            compact
            icon={FaTrash}
            variant="danger"
            onClick={() =>
              onDelete(objectif)
            }
            title="Supprimer"
          />
        </div>
      </div>

      <div className="mt-5">
        <Progress
          value={
            objectif.progress
          }
        />

        {!objectif.is_done ? (
          <input
            type="range"
            min="0"
            max="100"
            value={
              Number(
                objectif.progress,
              ) || 0
            }
            onChange={(event) =>
              onChangeProgress(
                objectif,
                Number(
                  event.target.value,
                ),
              )
            }
            className="mt-3 w-full accent-[#013E8E]"
          />
        ) : null}
      </div>
    </article>
  );
}

export default function ObjectifsSaisonDisplay({
  userId,
  onCounts,
}) {
  const [loading, setLoading] =
    useState(false);

  const [adherents, setAdherents] =
    useState([]);

  const [adherentId, setAdherentId] =
    useState(null);

  const [rows, setRows] =
    useState([]);

  const [
    modalOpen,
    setModalOpen,
  ] = useState(false);

  const [editing, setEditing] =
    useState(null);

  /* ---------------------------------------------------------------------- */
  /* Adhérents                                                              */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!userId) return;

      try {
        const data =
          await getAdherentsByUser(
            userId,
          );

        if (!mounted) return;

        const sorted = (
          Array.isArray(data)
            ? data
            : []
        ).sort((a, b) => {
          const byName = String(
            a.nom || "",
          ).localeCompare(
            String(
              b.nom || "",
            ),
            "fr",
            {
              sensitivity: "base",
            },
          );

          if (byName !== 0) {
            return byName;
          }

          return String(
            a.prenom || "",
          ).localeCompare(
            String(
              b.prenom || "",
            ),
            "fr",
            {
              sensitivity: "base",
            },
          );
        });

        setAdherents(sorted);

        if (sorted.length === 1) {
          setAdherentId(
            sorted[0].id,
          );
        }
      } catch (error) {
        console.error(error);

        toast.error(
          "Impossible de charger vos adhérents.",
        );
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [userId]);

  /* ---------------------------------------------------------------------- */
  /* Objectifs                                                              */
  /* ---------------------------------------------------------------------- */

  const loadRows = useCallback(
    async (id) => {
      if (!id) {
        setRows([]);
        return;
      }

      setLoading(true);

      try {
        const data =
          await getObjectifsByAdherent(
            id,
          );

        const list = Array.isArray(
          data,
        )
          ? data
          : [];

        setRows(list);

        onCounts?.({
          total: list.filter(
            (objectif) =>
              !objectif.is_done,
          ).length,
        });
      } catch (error) {
        console.error(error);

        toast.error(
          "Erreur lors du chargement des objectifs.",
        );
      } finally {
        setLoading(false);
      }
    },
    [onCounts],
  );

  useEffect(() => {
    if (adherentId) {
      loadRows(adherentId);
    }
  }, [
    adherentId,
    loadRows,
  ]);

  const currentAdherent =
    useMemo(
      () =>
        adherents.find(
          (adherent) =>
            Number(
              adherent.id,
            ) ===
            Number(
              adherentId,
            ),
        ) || null,
      [
        adherents,
        adherentId,
      ],
    );

  /* ---------------------------------------------------------------------- */
  /* Actions                                                                */
  /* ---------------------------------------------------------------------- */

  const openCreate = () => {
    if (!adherentId) {
      toast.info(
        "Sélectionnez d’abord un adhérent.",
      );

      return;
    }

    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (objectif) => {
    setEditing(objectif);
    setModalOpen(true);
  };

  const handleSubmitModal =
    async (
      payload,
      idToUpdate,
    ) => {
      try {
        const finalPayload = {
          ...payload,

          adherent_id:
            payload.adherent_id ||
            adherentId,
        };

        if (idToUpdate) {
          await updateObjectif(
            idToUpdate,
            finalPayload,
          );

          toast.success(
            "Objectif mis à jour.",
          );
        } else {
          await createObjectif(
            finalPayload,
          );

          toast.success(
            "Objectif créé.",
          );
        }

        setModalOpen(false);
        setEditing(null);

        await loadRows(
          adherentId,
        );
      } catch (error) {
        console.error(error);

        toast.error(
          "Échec de l’enregistrement de l’objectif.",
        );
      }
    };

  const handleDelete =
    async (objectif) => {
      if (
        !window.confirm(
          `Supprimer l’objectif « ${objectif.title} » ?`,
        )
      ) {
        return;
      }

      try {
        await deleteObjectif(
          objectif.id,
        );

        toast.success(
          "Objectif supprimé.",
        );

        await loadRows(
          adherentId,
        );
      } catch {
        toast.error(
          "Impossible de supprimer cet objectif.",
        );
      }
    };

  const handleToggleDone =
    async (objectif) => {
      try {
        await setObjectifDone(
          objectif.id,
          !objectif.is_done,
        );

        await loadRows(
          adherentId,
        );
      } catch {
        toast.error(
          "Impossible de changer l’état de l’objectif.",
        );
      }
    };

  const handleChangeProgress =
    async (
      objectif,
      value,
    ) => {
      setRows((previous) =>
        previous.map((row) =>
          row.id === objectif.id
            ? {
                ...row,

                progress: value,

                is_done:
                  value >= 100
                    ? true
                    : row.is_done,
              }
            : row,
        ),
      );

      try {
        await setObjectifProgress(
          objectif.id,
          value,
        );
      } catch {
        toast.error(
          "Échec de la mise à jour de la progression.",
        );
      }
    };

  return (
    <div className="space-y-5">
      {/* -------------------------------------------------------------- */}
      {/* Toolbar                                                        */}
      {/* -------------------------------------------------------------- */}

      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-950/40 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex-1">
          <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
            Adhérent
          </label>

          <select
            value={
              adherentId || ""
            }
            onChange={(event) =>
              setAdherentId(
                Number(
                  event.target.value,
                ),
              )
            }
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none focus:border-blue-300 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:focus:ring-blue-500/10 sm:max-w-sm"
          >
            <option
              value=""
              disabled
            >
              Sélectionner un
              adhérent
            </option>

            {adherents.map(
              (adherent) => (
                <option
                  key={adherent.id}
                  value={adherent.id}
                >
                  {String(
                    adherent.nom ||
                      "",
                  ).toUpperCase()}{" "}
                  {adherent.prenom}
                </option>
              ),
            )}
          </select>

          {currentAdherent ? (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Objectifs de{" "}
              <strong className="text-slate-700 dark:text-slate-200">
                {String(
                  currentAdherent.nom ||
                    "",
                ).toUpperCase()}{" "}
                {
                  currentAdherent.prenom
                }
              </strong>
            </p>
          ) : null}
        </div>

        <DashboardActionButton
          icon={FaPlus}
          variant="primary"
          onClick={openCreate}
          disabled={!adherentId}
        >
          Nouvel objectif
        </DashboardActionButton>
      </div>

      {/* -------------------------------------------------------------- */}
      {/* Liste                                                          */}
      {/* -------------------------------------------------------------- */}

      {loading ? (
        <div className="grid gap-3 md:grid-cols-2">
          {Array.from({
            length: 4,
          }).map((_, index) => (
            <div
              key={index}
              className="h-40 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
            />
          ))}
        </div>
      ) : !adherentId ? (
        <DashboardEmptyState
          icon={FaBullseye}
          title="Sélectionnez un adhérent"
          description="Choisissez d’abord la personne pour laquelle vous souhaitez consulter les objectifs."
        />
      ) : !rows.length ? (
        <DashboardEmptyState
          icon={FaBullseye}
          title="Aucun objectif"
          description="Vous pouvez créer un premier objectif de saison pour cet adhérent."
          action={
            <DashboardActionButton
              icon={FaPlus}
              variant="primary"
              onClick={openCreate}
            >
              Créer un objectif
            </DashboardActionButton>
          }
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {rows.map(
            (objectif) => (
              <ObjectifCard
                key={objectif.id}
                objectif={
                  objectif
                }
                onEdit={openEdit}
                onDelete={
                  handleDelete
                }
                onToggleDone={
                  handleToggleDone
                }
                onChangeProgress={
                  handleChangeProgress
                }
              />
            ),
          )}
        </div>
      )}

      <CreateEditObjectifModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={
          handleSubmitModal
        }
        initial={editing}
        adherentId={adherentId}
      />
    </div>
  );
}